/**
 * Vue directive that transforms empty/whitespace-only input values into numeric zero (0).
 *
 * Typical usage in templates:
 * <input v-auto-zero />
 *
 * The directive listens to the "input" event and, when the input's value is an empty string
 * or only whitespace, sets the element's value to 0 and dispatches an input event so Vue's
 * v-model and other listeners receive the update.
 *
 * @namespace AutoZeroDirective
 */
export default {
  /**
   * Hook called when the directive is bound to the element.
   *
   * @param {HTMLElement} el - The element the directive is bound to. Usually an <input>.
   * @param {Object} binding - An object containing the binding options passed to the directive (unused).
   * @param {*} binding.value - The value passed to the directive (not used by this implementation).
   * @param {Object} vnode - The Vue Virtual DOM node.
   * @returns {void}
   */
  bind(el, binding, vnode) {
    /**
     * Input event handler: normalizes empty or whitespace-only values to 0.
     *
     * @param {Event} event - The DOM input event.
     * @returns {void}
     */
    el.addEventListener("input", (event) => {
      const value = event.target.value;
      if (typeof value === "string" && value.trim() === "") {
        event.target.value = 0;
        // Dispatch a new input event so that Vue's v-model and other listeners react to the change.
        event.target.dispatchEvent(new Event("input"));
      }
    });
  },
};